import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  QrCode, Bell, Wifi, WifiOff, LayoutDashboard, BarChart3,
  Plug, Smartphone, Settings2, ArrowRight, Check, Clock, Shield, TrendingUp
} from 'lucide-react';
import { containerVariants, itemVariants } from '../utils/animations';

const features = [
  {
    icon: <Clock className="w-7 h-7 text-primary-600" />,
    title: 'Fraction-of-a-Second Scanning',
    description: 'AttendTrack marks attendance in milliseconds via barcode/QR scan — zero delays, zero queues at entry or exit.'
  },
  {
    icon: <Bell className="w-7 h-7 text-primary-600" />,
    title: 'Real-Time WhatsApp Notifications',
    description: 'Parents receive instant WhatsApp alerts the moment their child checks in or out — no app install required on their end.'
  },
  {
    icon: <QrCode className="w-7 h-7 text-primary-600" />,
    title: 'Barcode & QR Scanner',
    description: 'Works with any standard barcode scanner or smartphone camera. Each student ID card doubles as their attendance token.'
  },
  {
    icon: <WifiOff className="w-7 h-7 text-primary-600" />,
    title: 'Offline Attendance Mode',
    description: 'No internet? No problem. Attendance is captured offline and auto-synced to the cloud the moment connectivity is restored.'
  },
  {
    icon: <LayoutDashboard className="w-7 h-7 text-primary-600" />,
    title: 'Admin Dashboard & Reports',
    description: 'Powerful admin panel with date-wise records, student-wise reports, class-wise summaries, and exportable attendance sheets.'
  },
  {
    icon: <BarChart3 className="w-7 h-7 text-primary-600" />,
    title: 'Real-Time Analytics',
    description: 'Live attendance stats, absentee alerts, monthly trends, and custom date-range reports — all in one place.'
  },
  {
    icon: <Plug className="w-7 h-7 text-primary-600" />,
    title: 'CRM & App Integration',
    description: 'Plug AttendTrack into your existing school ERP, education app, or CRM via REST API — no rip-and-replace needed.'
  },
  {
    icon: <Settings2 className="w-7 h-7 text-primary-600" />,
    title: 'Fully Customizable',
    description: 'White-label ready. Customize branding, notification templates, report formats, and workflows to match your institution.'
  }
];

const useCases = [
  { emoji: '🏫', label: 'Schools & CBSE Boards' },
  { emoji: '📚', label: 'Coaching Institutes' },
  { emoji: '🎓', label: 'Colleges & Universities' },
  { emoji: '🏋️', label: 'Fitness & Yoga Studios' },
  { emoji: '💼', label: 'Corporate Training' },
  { emoji: '🏢', label: 'Any Business with Members' }
];

const stats = [
  { value: '<1s', label: 'Attendance Scan Time' },
  { value: '100%', label: 'Real-Time Sync' },
  { value: '24/7', label: 'System Uptime' },
  { value: '∞', label: 'Students Supported' }
];

const ScholarsAttendance: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Scholars Attendance – Real-Time Attendance App | Softconcern Technologies</title>
        <meta
          name="description"
          content="Scholars Attendance by Softconcern Technologies — a real-time attendance management app for schools, coaching institutes & businesses. Barcode scanning, WhatsApp notifications, offline mode, admin dashboard & CRM integration."
        />
        <meta
          name="keywords"
          content="scholars attendance, attendance app for schools, real-time attendance, WhatsApp attendance notification, barcode attendance, QR attendance, coaching institute attendance, student attendance management, AttendTrack, Softconcern"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Softconcern: Scholars Attendance" />
        <meta
          property="og:description"
          content="Real-time student attendance with barcode scanning, instant WhatsApp alerts, offline mode, and a powerful admin dashboard. Built for schools, coaching institutes & beyond."
        />
        <meta property="og:url" content="https://softconcern.com/scholars-attendance" />
        <meta property="og:site_name" content="Softconcern Technologies" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Softconcern: Scholars Attendance" />
        <meta
          name="twitter:description"
          content="Fraction-of-a-second barcode attendance, real-time WhatsApp notifications & powerful admin reports — for schools, coaching institutes & any business."
        />
        <link rel="canonical" href="https://softconcern.com/scholars-attendance" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Scholars Attendance",
          "applicationCategory": "EducationApplication",
          "operatingSystem": "Android, iOS, Web",
          "description": "Real-time student attendance management system with barcode scanning, WhatsApp notifications, offline mode, and admin dashboard.",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
          "author": {
            "@type": "Organization",
            "name": "Softconcern Technologies LLP",
            "url": "https://softconcern.com"
          }
        })}</script>
      </Helmet>

      {/* ── Hero ── */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-purple-50">
        <div className="container">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-6">
              <Smartphone className="w-4 h-4" />
              AttendTrack System · by Softconcern Technologies
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Scholars{' '}
              <span className="text-primary-600">Attendance</span>
              <br />
              <span className="text-3xl sm:text-4xl lg:text-5xl text-gray-700">Real-Time. Instant. Reliable.</span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10"
            >
              The smartest attendance management system for schools, coaching institutes, and any business.
              Scan a barcode in a fraction of a second, trigger an instant WhatsApp notification to parents,
              and let admins track every student's record in real time — online or offline.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://softconcern.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-primary-600 text-primary-600 font-medium rounded-lg hover:bg-primary-50 transition-colors duration-200"
              >
                Visit Softconcern.com
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-12 bg-primary-600">
        <div className="container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-4xl font-bold mb-1">{s.value}</div>
                <div className="text-primary-100 text-sm">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About the Product ── */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium mb-4">
                About Scholars Attendance
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
                Built by Softconcern Technologies for the Modern Classroom
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                <strong>Softconcern Technologies LLP</strong> is an AI-driven software company based in India,
                empowering businesses with innovative digital solutions since 2019. Our flagship product,
                <strong> Scholars Attendance</strong>, was purpose-built to eliminate the pain of manual
                attendance registers in schools and coaching institutes.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Powered by the <strong>AttendTrack</strong> engine, the system captures attendance in a
                fraction of a second using barcode or QR scanning, fires real-time WhatsApp messages to
                parents, and gives administrators a crystal-clear dashboard of every student's presence —
                all without depending on a constant internet connection.
              </p>
              <div className="space-y-3">
                {[
                  'Trusted by schools & coaching institutes across India',
                  'Works on Android, iOS, and Web browsers',
                  'No hardware lock-in — use any barcode scanner',
                  'GDPR & data-privacy compliant architecture'
                ].map((pt, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700">{pt}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-2xl p-8 text-center"
            >
              <div className="text-7xl mb-4">📲</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">AttendTrack System</h3>
              <p className="text-gray-600 mb-6">
                The core engine behind Scholars Attendance — designed for speed, reliability, and scale.
              </p>
              <div className="grid grid-cols-2 gap-4 text-left">
                {[
                  ['⚡', 'Sub-second scan'],
                  ['💬', 'WhatsApp alerts'],
                  ['📴', 'Offline mode'],
                  ['📊', 'Live dashboard']
                ].map(([emoji, label], i) => (
                  <div key={i} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                    <span className="text-xl">{emoji}</span>
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Attendance
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From the moment a student walks in to the report an admin downloads at month-end —
              Scholars Attendance handles it all.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((f, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-xl mx-auto">Three steps. Fraction of a second. Done.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', emoji: '🪪', title: 'Student Scans ID', desc: 'Student taps or scans their barcode/QR ID card at the entry or exit point.' },
              { step: '02', emoji: '⚡', title: 'Instant Attendance Marked', desc: 'AttendTrack records the timestamp in under a second — online or offline.' },
              { step: '03', emoji: '💬', title: 'Parent Gets WhatsApp Alert', desc: 'A real-time WhatsApp message is sent to the parent with in/out time instantly.' }
            ].map((s, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {s.step}
                </div>
                <div className="text-4xl mb-3">{s.emoji}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="section-padding bg-primary-50">
        <div className="container">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
              <TrendingUp className="w-4 h-4" /> Trending Across Industries
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              One App. Any Business.
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Scholars Attendance isn't limited to schools. Any organisation that tracks member or
              employee presence can plug in AttendTrack and go live in hours.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {useCases.map((u, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-5 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="text-3xl mb-2">{u.emoji}</div>
                <p className="text-sm font-medium text-gray-700">{u.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Integration ── */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-gray-50 to-primary-50 rounded-2xl p-8"
            >
              <div className="text-6xl mb-4 text-center">🔌</div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Plug Into Your Existing Stack</h3>
              <div className="space-y-3">
                {[
                  'REST API for any CRM or ERP integration',
                  'Webhook support for real-time event streaming',
                  'Compatible with popular education platforms',
                  'Custom SDK available for mobile app embedding',
                  'Single Sign-On (SSO) support'
                ].map((pt, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{pt}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium mb-4">
                Seamless Integration
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
                Connect to Any Existing System
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Already using a school management system, education app, or CRM? Scholars Attendance
                integrates seamlessly via our open API — no need to replace your existing tools.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our team at Softconcern Technologies provides full integration support, custom development,
                and white-label options so the product feels native to your platform.
              </p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Talk to Our Team <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Trust Signals ── */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Institutions Trust Scholars Attendance
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Shield className="w-6 h-6 text-primary-600" />, title: 'Robust & Secure', desc: 'Enterprise-grade security with encrypted data storage and secure API endpoints.' },
              { icon: <Wifi className="w-6 h-6 text-primary-600" />, title: 'Online + Offline', desc: 'Never miss an attendance entry — works seamlessly with or without internet.' },
              { icon: <TrendingUp className="w-6 h-6 text-primary-600" />, title: 'Trending Solution', desc: 'Adopted by growing institutions across India as the go-to attendance platform.' },
              { icon: <BarChart3 className="w-6 h-6 text-primary-600" />, title: 'Actionable Reports', desc: 'Date-wise, student-wise, and class-wise reports exportable in PDF and Excel.' },
              { icon: <Settings2 className="w-6 h-6 text-primary-600" />, title: 'Fully Customizable', desc: 'Tailor every aspect — branding, notifications, workflows — to your institution.' },
              { icon: <Smartphone className="w-6 h-6 text-primary-600" />, title: 'Multi-Platform', desc: 'Available on Android, iOS, and Web. Manage attendance from any device, anywhere.' }
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mb-3">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section-padding bg-primary-600">
        <div className="container">
          <motion.div
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Modernise Your Attendance System?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Join schools and institutes already using Scholars Attendance by Softconcern Technologies.
              Get a free demo today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center gap-2"
              >
                Get a Free Demo <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="mailto:softconcern@gmail.com"
                className="border-2 border-white text-white hover:bg-primary-700 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center gap-2"
              >
                softconcern@gmail.com
              </a>
            </div>
            <p className="mt-6 text-primary-200 text-sm">
              Softconcern Technologies LLP · softconcern.com · +91 9876543210
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ScholarsAttendance;
